import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCases, approveCaseLocal, approveCaseMonthly, approveCaseOrg, closeCase } from '../../../services/cases';
import DataTable from '../../../components/Tables/DataTable';
import { useAuth } from '../../../hooks/useAuth';
import Toast from '../../../components/Common/Toast';
import { 
  FilePlus, 
  Search, 
  Briefcase, 
  CheckCircle2, 
  Clock, 
  XCircle, 
  ArrowUpCircle,
  Eye,
  AlertCircle
} from 'lucide-react';

const CaseList = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [toast, setToast] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    loadCases();
  }, []);

  const loadCases = async () => {
    try {
      setLoading(true);
      const data = await getCases();
      // Debug point: Check your console to see the JSON structure
      console.log("Cases Data:", data); 
      setCases(Array.isArray(data) ? data : []);
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to synchronize cases' });
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (id, action, label) => {
    try {
      if (action === 'approve_local') await approveCaseLocal(id);
      else if (action === 'approve_monthly') await approveCaseMonthly(id);
      else if (action === 'approve_org') await approveCaseOrg(id);
      else if (action === 'close') await closeCase(id);
      
      setToast({ type: 'success', message: `Case ${label} successfully` });
      loadCases();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.error || 'Action failed' });
    }
  };

  // Improved Filtering Logic
  const filteredCases = cases.filter(c => {
    const search = searchTerm.toLowerCase();
    // Resilient name checking for the filter
    const memberObj = c?.member || c?.Member;
    const userObj = memberObj?.User || memberObj?.user;
    const name = (userObj?.full_name || memberObj?.full_name || "").toLowerCase();
    const caseType = (c.type || "").toLowerCase();
    
    return name.includes(search) || caseType.includes(search);
  });

  const getStatusBadge = (status) => {
    const config = {
      pending_local: { label: 'Local Review', bg: 'bg-amber-50 text-amber-700 border-amber-200', icon: <Clock size={12}/> },
      pending_monthly: { label: 'Monthly Review', bg: 'bg-blue-50 text-blue-700 border-blue-200', icon: <Clock size={12}/> },
      approved: { label: 'Awaiting Opening', bg: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: <CheckCircle2 size={12}/> },
      open: { label: 'Active Case', bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: <ArrowUpCircle size={12}/> },
      closed: { label: 'Closed', bg: 'bg-slate-50 text-slate-500 border-slate-200', icon: <XCircle size={12}/> },
    };
    const item = config[status] || { label: status?.replace('_', ' ') || 'Unknown', bg: 'bg-slate-50', icon: null };
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${item.bg}`}>
        {item.icon} {item.label}
      </span>
    );
  };

  const renderActions = (row) => {
    const btnBase = "flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all active:scale-95 shadow-sm whitespace-nowrap";
    
    switch (row?.status) {
      case 'pending_local':
        return (
          <button onClick={() => handleAction(row.id, 'approve_local', 'locally approved')} 
            className={`${btnBase} bg-amber-600 text-white hover:bg-amber-700`}>
            Approve Local
          </button>
        );
      case 'pending_monthly':
        return (
          <button onClick={() => handleAction(row.id, 'approve_monthly', 'monthly approved')} 
            className={`${btnBase} bg-blue-600 text-white hover:bg-blue-700`}>
            Approve Monthly
          </button>
        );
      case 'approved':
        return (
          <button onClick={() => handleAction(row.id, 'approve_org', 'opened')} 
            className={`${btnBase} bg-indigo-600 text-white hover:bg-indigo-700`}>
            Open Case
          </button>
        );
      case 'open':
        return (
          <button onClick={() => handleAction(row.id, 'close', 'closed')} 
            className={`${btnBase} border border-rose-200 text-rose-600 hover:bg-rose-50`}>
            Close Case
          </button>
        );
      default:
        return null;
    }
  };

  const columns = [
    { 
      key: 'type', 
      label: 'Case Identity',
      render: (_, row) => (
        <div className="flex flex-col min-w-[120px]">
          <span className="font-bold text-slate-900 capitalize">
            {row?.type?.replace('_', ' ') || 'General Case'}
          </span>
          <span className="text-[10px] font-mono text-slate-400">Ref: #{row?.id || '---'}</span>
        </div>
      )
    },
    { 
      key: 'member_name', 
      label: 'Beneficiary',
      render: (_, row) => {
        // RESILIENT ACCESSOR: Checks for common naming conventions (camelCase vs PascalCase)
        const member = row?.member || row?.Member;
        const user = member?.User || member?.user;
        
        const name = user?.full_name || member?.full_name || 'Unassigned Member';
        const id = member?.member_number || '---';

        return (
          <div className="flex flex-col">
            <span className="font-bold text-slate-700">{name}</span>
            <span className="text-[10px] text-slate-400 font-mono tracking-tighter">
              ID: {id}
            </span>
          </div>
        );
      }
    },
    { 
      key: 'admin_unit', 
      label: 'Local Church',
      render: (_, row) => {
        const member = row?.member || row?.Member;
        const unit = member?.AdminUnit || member?.admin_unit;
        return (
          <div className="flex flex-col">
            <span className="text-sm font-medium text-slate-700">
              {unit?.name || 'N/A'}
            </span>
            {unit?.code && (
              <span className="text-[10px] text-slate-400 font-mono">
                Code: {unit.code}
              </span>
            )}
          </div>
        );
      }
    },
    { 
      key: 'target_amount', 
      label: 'Amount (KSh)',
      render: (val) => (
        <span className="font-black text-slate-900 text-sm">
          {val ? Number(val).toLocaleString() : '0.00'}
        </span>
      )
    },
    { 
      key: 'status', 
      label: 'Status', 
      render: (val) => getStatusBadge(val) 
    },
    { 
      key: 'actions', 
      label: 'Management', 
      render: (_, row) => (
        <div className="flex items-center gap-3">
          {renderActions(row)}
          <Link 
            to={`/dashboard/org-admin/cases/${row?.id}`} 
            className="p-2 text-slate-300 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            title="View Full Case"
          >
            <Eye size={18} />
          </Link>
        </div>
      ) 
    },
  ];

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-slate-100 pb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Briefcase size={16} className="text-indigo-600" />
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 italic">Welfare Management</span>
          </div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">Case Directory</h1>
          <p className="text-slate-500 font-medium mt-1">Review, approve, and finalize welfare distribution requests.</p>
        </div>
        <Link to="/dashboard/org-admin/cases/new">
          <button className="flex items-center gap-2 bg-slate-900 text-white px-8 py-4 rounded-2xl font-bold hover:bg-indigo-600 transition-all shadow-xl shadow-slate-200 active:scale-95">
            <FilePlus size={20} />
            Create New Case
          </button>
        </Link>
      </div>

      {/* SEARCH TOOLBAR */}
      <div className="max-w-md relative group">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
        <input 
          type="text" 
          placeholder="Filter by member name or case type..."
          className="w-full pl-12 pr-4 py-4 bg-white border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 transition-all text-sm shadow-sm"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* DATA TABLE WRAPPER */}
      <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-xl shadow-slate-100/50 overflow-hidden min-h-[400px]">
        <DataTable columns={columns} data={filteredCases} loading={loading} />
        
        {!loading && filteredCases.length === 0 && (
          <div className="py-24 text-center">
            <AlertCircle size={48} className="mx-auto text-slate-100 mb-4" />
            <p className="text-slate-900 font-black italic text-lg">No cases found</p>
            <p className="text-slate-400 text-sm max-w-xs mx-auto">There are no records matching your current filter. Try adjusting your search term.</p>
          </div>
        )}
      </div>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
};

export default CaseList;